// Sprint 09H-B — organizer Web Push (VAPID) subscription lifecycle. One small,
// framework-independent module (mirrors the pattern already established by
// companion-app/src/lib/pwaInstall.js: pure/imperative logic kept separate from the
// component that renders it), so it can be reasoned about without mounting anything.
// Permission is only ever requested from subscribeAdminPush(), which every caller in
// this app only ever invokes from an explicit button click (§40: never automatically
// on page load).

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

function sameKey(a, b) {
  const aBytes = a instanceof ArrayBuffer ? new Uint8Array(a) : a;
  const bBytes = b instanceof ArrayBuffer ? new Uint8Array(b) : b;
  if (!aBytes || !bBytes || aBytes.length !== bBytes.length) return false;
  for (let i = 0; i < aBytes.length; i += 1) {
    if (aBytes[i] !== bBytes[i]) return false;
  }
  return true;
}

export function isPushSupported() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;
}

export async function getExistingAdminSubscription() {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function subscribeAdminPush(authAxios) {
  if (!isPushSupported()) throw new Error("Push wordt niet ondersteund in deze browser");
  const { data } = await authAxios.get("/push/vapid-public-key");
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.public_key),
  });
  const json = sub.toJSON();
  await authAxios.post("/admin/push/subscribe", {
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
  });
  return sub;
}

export async function unsubscribeAdminPush(authAxios) {
  const sub = await getExistingAdminSubscription();
  if (!sub) return;
  try {
    await authAxios.delete("/admin/push/subscribe", { params: { endpoint: sub.endpoint } });
  } finally {
    await sub.unsubscribe();
  }
}

// Production fix (§10/§11): a browser PushSubscription is permanently bound to the
// applicationServerKey (VAPID public key) it was created with — if the backend's
// VAPID keypair is ever rotated after a device already subscribed, that device's
// existing subscription silently becomes useless (every push the server sends will
// be rejected as "VAPID credentials do not correspond"), with nothing in the
// browser UI hinting at this. This detects that mismatch and repairs it: since
// Notification permission was already granted when the stale subscription was
// created, re-subscribing here needs no new user gesture/prompt. Never touches
// permission state itself — a denied permission is left alone (§11: "niet opnieuw
// vragen").
export async function ensureAdminPushSubscription(authAxios) {
  if (!isPushSupported()) return { state: "unsupported" };
  if (typeof Notification !== "undefined" && Notification.permission !== "granted") {
    return { state: "no_permission" };
  }
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return { state: "no_registration" };
  const existing = await reg.pushManager.getSubscription();
  if (!existing) return { state: "none" };

  let publicKey;
  try {
    const { data } = await authAxios.get("/push/vapid-public-key");
    publicKey = data.public_key;
  } catch {
    // Can't reach the backend right now — leave the existing subscription alone
    // rather than guessing; try again next time this is called.
    return { state: "check_failed", subscription: existing };
  }
  const currentKeyBytes = urlBase64ToUint8Array(publicKey);

  if (sameKey(existing.options?.applicationServerKey, currentKeyBytes)) {
    return { state: "ok", subscription: existing };
  }

  await existing.unsubscribe();
  const fresh = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: currentKeyBytes });
  const json = fresh.toJSON();
  await authAxios.post("/admin/push/subscribe", {
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
  });
  return { state: "resynced", subscription: fresh };
}
